/**
 * Conservative event finder for free trip text ("... for a Mack Geiger concert
 * on Thu Dec 10, 2026, with ...").
 *
 * It only ever PROPOSES: a result is shown as unconfirmed and written only when
 * the traveller taps Confirm. To keep a wrong proposal rarer than a missing one
 * it accepts a date only when ALL hold:
 *   - an explicit month + day (or an ISO date) sits in the same sentence as an
 *     event noun (concert, wedding, ...);
 *   - if a weekday is written, it matches that calendar date;
 *   - a missing year is taken from the trip's own flight year, never "today";
 *   - the date falls within a week of the flight window.
 */
import { addDays, diffDays, toDay, weekdayOf } from "./tripCalendarView";

export interface TextEvent {
  date: string;
  title: string;
}

const NOUNS =
  "concert|show|game|match|wedding|meeting|conference|festival|reunion|recital|graduation|tournament|race|fight|gala|summit|party|funeral|retreat|convention|expo";
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const WEEKDAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

const NOUN_RE = new RegExp(`\\b(${NOUNS})s?\\b`, "i");
const MD_RE =
  /\b(?:(sun|mon|tue|wed|thu|fri|sat)[a-z]*\.?,?\s+)?(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s+(\d{4}))?\b/i;
const ISO_RE = /\b(\d{4}-\d{2}-\d{2})\b/;

function titleFor(sentence: string): string {
  const m = new RegExp(`((?:[A-Z][\\w'’.-]*\\s+){1,4})(${NOUNS})\\b`).exec(sentence);
  if (m) return `${m[1].trim()} ${m[2].toLowerCase()}`;
  const noun = NOUN_RE.exec(sentence)![1].toLowerCase();
  return noun[0].toUpperCase() + noun.slice(1);
}

export function findTextEvent(
  text: string | null | undefined,
  flightDates: string[],
): TextEvent | null {
  const days = flightDates.map(toDay).filter((d): d is string => d !== null).sort();
  if (!text || days.length === 0) return null;
  const from = days[0];
  const to = days[days.length - 1];
  const refYear = Number(from.slice(0, 4));

  for (const sentence of text.split(/(?<=[.!?])\s+(?=[A-Z])|\n+/)) {
    if (!NOUN_RE.test(sentence)) continue;
    let date: string | null = null;
    const iso = ISO_RE.exec(sentence);
    if (iso) {
      date = toDay(iso[1]);
    } else {
      const m = MD_RE.exec(sentence);
      if (m) {
        const month = MONTHS.indexOf(m[2].toLowerCase()) + 1;
        const day = Number(m[3]);
        const year = m[4] ? Number(m[4]) : refYear;
        const cand = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        // Reject impossible dates such as Feb 30 (they roll over in UTC math).
        if (day >= 1 && addDays(cand, 0) === cand) {
          if (!m[1] || WEEKDAYS[weekdayOf(cand)] === m[1].toLowerCase()) date = cand;
        }
      }
    }
    if (!date) continue;
    if (diffDays(from, date) < -7 || diffDays(date, to) < -7) continue;
    return { date, title: titleFor(sentence) };
  }
  return null;
}
